import * as classnames from 'classnames';
import * as React from 'react';
import * as AriaModal from 'react-aria-modal';
import styled from 'styled-components';
import { breakpoint } from '@estee/elc-base-theme';
import { responsiveModalTheme, responsiveModalMaskTheme } from '../theme/default-theme';
import { LayoutPosition } from '@estee/elc-layouts';

interface IMaskProps {
    isOpen: boolean;
    position?: LayoutPosition;
}

const Mask = styled.div<IMaskProps>`
    position: fixed;
    z-index: 99999;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    overflow: scroll;
    display: ${(props: { isOpen: boolean }) => (props.isOpen ? 'flex' : 'hidden')};
    ${responsiveModalMaskTheme};
    flex-direction: column;
    justify-content: flex-end;
    ${({ position }) => position === LayoutPosition.TOP && `justify-content: flex-start;`}
    ${({ position }) => position === LayoutPosition.MIDDLE && `justify-content: center;`}
    ${({ position }) => position === LayoutPosition.BOTTOM && `justify-content: flex-end;`}
`;

const Modal = styled.div`
    position: absolute;
    top: 22%;
    left: 50%;
    padding: 20px;
    transform: translateX(-50%) translateY(-20%);
    width: 100%;
    ${responsiveModalTheme};
    ${breakpoint('desktop')`
        width: auto; 
    `};

    &.full-width {
        width: 100%;
    }
`;
export interface IResponsiveModalProps {
    isOpen: boolean;
    title?: string;
    className?: string;
    deactivateModal?(): void;
    maskClassName?: string;
    onMaskClick?(event: React.MouseEvent<HTMLDivElement>): void;
    underlayClickExits?: boolean;
    underlayStyle?: object;
    position?: LayoutPosition;
    underlayClassName?: string;
}

export interface IResponsiveModalState {
    isOpen: boolean;
}

export interface IReactMouseEvent extends React.MouseEvent<HTMLDivElement> {
    target: HTMLElement & EventTarget;
}

const RESPONSIVE_MODAL_CLASSNAME = 'elc-responsive-modal-mask';

export class ResponsiveModal extends React.Component<IResponsiveModalProps, IResponsiveModalState> {
    public ariaModalDefaultTitle: IResponsiveModalProps['title'] = 'modal';

    private handleMaskClick = (event: IReactMouseEvent) => {
        this.handleEventPropagation(event);
        const { onMaskClick } = this.props;
        if (onMaskClick && event.target.classList.contains(RESPONSIVE_MODAL_CLASSNAME)) {
            onMaskClick(event);
        }
    };

    private handleEventPropagation(e: IReactMouseEvent) {
        e.stopPropagation();
    }

    public render() {
        if (!this.props.isOpen) {
            return null;
        }

        const {
            title,
            deactivateModal,
            underlayStyle,
            position,
            underlayClickExits = true
        } = this.props;

        const modalClassNames = classnames(
            this.props.className,
            'elc-responsive-modal',
            'js-responsive-modal'
        );

        const maskClassNames = classnames(
            this.props.maskClassName,
            RESPONSIVE_MODAL_CLASSNAME,
            'js-responsive-modal-mask',
            // https://jira.esteeonline.com/browse/SDGLTMPL-1472
            'isOpen'
        );

        const underlayClassNames = classnames(
            this.props.underlayClassName,
            `${RESPONSIVE_MODAL_CLASSNAME}-underlay`
        );

        return (
            <AriaModal
                titleText={title || this.ariaModalDefaultTitle}
                onExit={deactivateModal}
                underlayStyle={underlayStyle}
                focusTrapPaused
                underlayClickExits={underlayClickExits}
                underlayClass={underlayClassNames}
            >
                <Mask
                    isOpen
                    className={maskClassNames}
                    onClick={this.handleMaskClick}
                    position={position}
                    onMouseDown={this.handleEventPropagation}
                >
                    <Modal className={modalClassNames}>{this.props.children}</Modal>
                </Mask>
            </AriaModal>
        );
    }
}
